import { FC, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { getErrorFromCatch } from "../../../api/api";
import { replacementApi } from "../../../api/replacement.api";
import { IOrderItem } from "../../../model/IOrder";
import { IReplacementCandidate, IReplacementSearch } from "../../../model/IReplacement";
import priceFormatter from "../../../utils/priceFormatter";
import Modal from "../../common/Modal";
import Table, { ITableTh } from "../../common/table/Table";
import Button from "../../form/Button";
import Input from "../../form/Input";

// --
// -- The old x-connector page's columns, in its order, so anyone who used that
// -- page recognises this one.
// --
const COLUMNS: ITableTh[] = [
  { title: 'Name' },
  { title: 'Part Number' },
  { title: 'Description' },
  { title: 'Location' },
  { title: 'Distance' },
  { title: 'Mileage' },
  { title: 'Price' },
  { title: 'Shipping' },
  { title: 'Tag#' },
  { title: 'Supplier' },
  { title: 'Stock#' },
  { title: 'Interchange Number' },
  { title: '', align: 'right' },
];

const money = (value: number | null) =>
  value === null || value === undefined ? '—' : priceFormatter(value);

const miles = (value: number | null) =>
  value === null || value === undefined ? '—' : `${Math.round(value)} mi`;

/**
 * What the list is showing, in words.
 *
 * Without this a short list is ambiguous - nothing comparable exists, or the
 * price ceiling cut it short - and the two look identical.
 */
const criteria = (search: IReplacementSearch): string => {
  if (search.partNumber) {
    return `Parts matching "${search.partNumber}". The price and mileage limits do not apply to a number search.`;
  }

  const parts: string[] = [`Interchange ${search.interchangeNumber}`];
  if (search.maxPrice != null) {
    parts.push(`up to ${priceFormatter(search.maxPrice)}`
      + (search.originalPrice != null ? ` (this part cost ${priceFormatter(search.originalPrice)})` : ''));
  }
  if (search.minMileage != null) {
    parts.push(`mileage ${search.minMileage.toLocaleString('en-US')} or more`);
  }
  if (search.distanceAvailable && search.zip) {
    parts.push(`distances from ${search.zip}`);
  }
  return `${parts.join(' · ')}.`;
};

/**
 * Pick a part to replace one already sold, and have the store re-create the
 * order around it.
 *
 * <p>The Create button does not place the order itself - the storefront does,
 * through the same {@code recreate_order_from_previous_order_with_pid} call the
 * old page used. It is not undoable from here, which is why it asks first.
 */
const CreateReplacementModal: FC<{
  orderId: string,
  item: IOrderItem,
  onClose: () => void,
}> = ({ orderId, item, onClose }) => {
  const navigate = useNavigate();

  const [search, setSearch] = useState<string>('');
  // The number actually searched for. Empty means the similar-parts list.
  const [partNumber, setPartNumber] = useState<string>('');

  const { data, isFetching, isError, error } = replacementApi.useGetReplacementCandidatesQuery({
    orderId,
    itemId: item.id,
    ...(partNumber ? { partNumber } : {}),
  });

  const [createReplacement, { isLoading: isCreating }] =
    replacementApi.useCreateReplacementMutation();

  const [confirming, setConfirming] = useState<IReplacementCandidate | null>(null);

  const runSearch = (term: string) => setPartNumber(term.trim());

  const handleCreate = async () => {
    if (!confirming) return;

    try {
      const result = await createReplacement({
        orderId,
        itemId: item.id,
        pid: confirming.pid,
      }).unwrap();

      // The store answers 200 either way: created:false carries its own
      // reason, which is the useful part - the part may have sold, or changed
      // price, or not ship to that address.
      if (!result.created || !result.orderId) {
        toast.error(result.error || 'The store did not create the replacement order.');
        setConfirming(null);
        return;
      }

      toast.success(`Replacement order ${result.orderId} created`);
      setConfirming(null);
      onClose();
      navigate(`/orders/${result.orderId}`);
    } catch (err) {
      toast.error(getErrorFromCatch(err) || 'Failed to create the replacement');
    }
  };

  return (
    <>
      <Modal
        isShown
        title={`Create Replacement${item.name ? ` — ${item.name}` : ''}`}
        style={{ width: '96vw', maxWidth: '96vw' }}
        onClose={onClose}
      >
        <div className="flex flex-col gap-4">
          <div className="flex justify-between gap-6 items-end flex-wrap">
            {/* --
                -- Run on Enter or on the button. Deliberately not a <form>:
                -- Button does not pass its `type` through to the element, so
                -- every button inside a form submits it.
                -- */}
            <div className="flex gap-2 items-end">
              <Input
                label="Search by part number"
                name="partNumber"
                value={search}
                placeholder="Part, stock or tag number"
                handle={(e) => setSearch((e as React.ChangeEvent<HTMLInputElement>).target.value)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') runSearch(search);
                }}
                disabled={isFetching}
                classNameField="min-w-[220px]"
              />
              <Button
                isNotFullWidth
                size="small"
                variant="outline"
                onClick={() => runSearch(search)}
              >
                Search
              </Button>
              {!!partNumber && (
                <Button
                  isNotFullWidth
                  size="small"
                  variant="outline"
                  onClick={() => {
                    setSearch('');
                    setPartNumber('');
                  }}
                >
                  Back to similar parts
                </Button>
              )}
            </div>

            <div className="text-text_pale">
              {data?.candidates?.length ?? 0} part(s)
              {data?.capped ? ' (limit reached — narrow the search)' : ''}
            </div>
          </div>

          {!!data && (
            <div className="text-text_pale">{criteria(data)}</div>
          )}

          {!!data && !data.interchangeNumber && !data.partNumber && (
            // Nothing to match on. Plenty of parts have no interchange number,
            // so this is not an error - but an empty table with no explanation
            // would read as one.
            <div className="text-red-600">
              This part has no interchange number, so there is nothing to match similar
              parts against. Search by part number instead.
            </div>
          )}

          {isError && (
            <div className="text-red-600">
              Could not load replacement parts. {getErrorFromCatch(error)}
            </div>
          )}

          {/* --
              -- A bounded height so the table scrolls in both directions with
              -- its header pinned. Thirteen columns do not fit a modal, and
              -- wrapping the whole table in overflow-x-auto would make THAT
              -- the scroll container - which, having no bounded height, never
              -- scrolls vertically and so unsticks the header.
              -- */}
          <div className="h-[60vh] flex flex-col min-h-0 -mx-10">
            <Table
              fillHeight
              size="small"
              isPending={isFetching}
              isDisablePaginate
              thead={COLUMNS}
              isResizable
              resizeStorageKey="replacement-candidates"
              tbody={(data?.candidates || []).map((candidate) => ({
                data: [
                  candidate.name || '—',
                  candidate.partNumber || '—',
                  <span className="whitespace-pre-wrap">{candidate.description || '—'}</span>,
                  candidate.location || '—',
                  miles(candidate.distance),
                  candidate.mileage || '—',
                  money(candidate.price),
                  money(candidate.shipping),
                  candidate.tagId || '—',
                  candidate.supplier || '—',
                  candidate.stockNumber || '—',
                  candidate.interchangeNumber || '—',
                  <Button
                    isNotFullWidth
                    size="small"
                    onClick={() => setConfirming(candidate)}
                  >
                    Create
                  </Button>,
                ],
              }))}
            />
          </div>
        </div>
      </Modal>

      {!!confirming && (
        <Modal
          isShown
          title="Create Replacement"
          style={{ maxWidth: '560px' }}
          onClose={() => setConfirming(null)}
        >
          <div className="flex flex-col gap-6">
            <div>
              <div>Do you really want to create the replacement?</div>
              <div className="mt-3">
                <b>{confirming.name || confirming.partNumber || confirming.pid}</b>
                {confirming.price != null ? ` — ${priceFormatter(confirming.price)}` : ''}
                {confirming.location ? ` — ${confirming.location}` : ''}
              </div>
              {/* It is the store that places the order, and nothing here can
                  take it back afterwards. */}
              <div className="mt-3 text-text_pale">
                This creates a new order on the same store as {orderId}, and cannot be
                undone from here.
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                isNotFullWidth
                disabled={isCreating}
                onClick={() => setConfirming(null)}
              >
                Cancel
              </Button>
              <Button
                isNotFullWidth
                type="submit"
                disabled={isCreating}
                onClick={handleCreate}
              >
                {isCreating ? 'Creating…' : 'Create Replacement'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default CreateReplacementModal;
